import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { tapResponse } from "@ngrx/operators";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { computed, inject } from "@angular/core";
import {
	MessageTemplate,
	MessageTemplateContext,
	MessageTemplateLanguage,
} from "../models/message-template.model";
import { MessageTemplateService } from "../services/message-template.lib.service";

type MessageTemplateUsageState = {
	templates: MessageTemplate[];
	isLoading: boolean;
	error: string | null;
	selectedTemplateKey: string | null;
	searchTerm: string;
	preferredLanguage: MessageTemplateLanguage;
	usageContext: MessageTemplateContext | null;
};

const initialState: MessageTemplateUsageState = {
	templates: [],
	isLoading: false,
	error: null,
	selectedTemplateKey: null,
	searchTerm: "",
	preferredLanguage: "de",
	usageContext: null,
};

export const MessageTemplateUsageStore = signalStore(
	{ providedIn: "root" },
	withState(initialState),
	withComputed((store) => ({
		filteredTemplates: computed(() => {
			const templates = store.templates();
			const searchTerm = store.searchTerm().toLowerCase();
			const context = store.usageContext();

			return templates
				.filter((t) => {
					if (!searchTerm.trim()) {
						return true;
					}
					const sanitizedSearchTerm = searchTerm.trim().toLowerCase();
					const words = sanitizedSearchTerm.split(" ");
					return (
						t.title.toLowerCase().includes(sanitizedSearchTerm) ||
						words.every((word) => t.tags?.some((tag) => tag.toLowerCase().includes(word))) ||
						t.key === sanitizedSearchTerm
					);
				})
				.filter((t) => {
					if (!context) {
						return true;
					}
					if (!t.usageContexts || t.usageContexts.length === 0) {
						return true;
					}
					return t.usageContexts.includes(context);
				});
		}),
		selectedTemplate: computed(() => {
			const key = store.selectedTemplateKey();
			if (!key) {
				return null;
			}
			return store.templates().find((t) => t.key === key) ?? null;
		}),
	})),
	withMethods((store, messageTemplateService: MessageTemplateService = inject(MessageTemplateService)) => ({
		fetchActiveTemplates: rxMethod<void>(
			pipe(
				tap(() => patchState(store, { isLoading: true })),
				switchMap(() =>
					messageTemplateService.getActiveTemplates().pipe(
						tapResponse({
							next: (templates: MessageTemplate[]) =>
								patchState(store, { templates, isLoading: false, error: null }),
							error: (error: Error) =>
								patchState(store, { error: error.message, isLoading: false }),
						})
					)
				)
			)
		),

		setTemplates: (templates: MessageTemplate[]) =>
			patchState(store, { templates, isLoading: false, error: null }),

		setLoading: (isLoading: boolean) => patchState(store, { isLoading }),

		setError: (error: string) => patchState(store, { error, isLoading: false }),

		selectTemplate: (key: string | null) => patchState(store, { selectedTemplateKey: key }),

		setSearchTerm: (searchTerm: string) => patchState(store, { searchTerm }),

		setPreferredLanguage: (language: MessageTemplateLanguage) =>
			patchState(store, { preferredLanguage: language }),

		setUsageContext: (context: MessageTemplateContext | null) =>
			patchState(store, { usageContext: context }),

		reset: () => patchState(store, initialState),
	})),
	withHooks({
		onInit(store) {
			store.fetchActiveTemplates();
		},
	})
);
